<template>
  <CompanyAppLayout
    page-title="CRM"
    page-subtitle="Customers, contracts, documents, and pricebook"
  >
    <div class="space-y-6 pt-4">
      <div class="flex flex-wrap gap-2">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          :class="activeTab === tab.id
            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
            : 'bg-slate-800/60 text-slate-300 border border-slate-700 hover:border-slate-500'"
          @click="switchTab(tab.id)"
        >
          {{ tab.label }}
          <span class="ml-2 text-xs text-slate-400">{{ counts[tab.id] ?? 0 }}</span>
        </button>
      </div>

      <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
        <div class="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <h2 class="text-xl font-semibold text-white">{{ currentTab.label }}</h2>
          <button
            v-if="currentTab.canCreate"
            class="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium"
            @click="createItem"
          >
            Add {{ currentTab.singular }}
          </button>
        </div>

        <p v-if="error" class="text-red-400 mb-4">{{ error }}</p>
        <p v-else-if="loading" class="text-slate-400">Loading…</p>

        <div v-else-if="!rows.length" class="text-slate-400 py-10 text-center">
          No {{ currentTab.label.toLowerCase() }} yet. Use Add to create the first record.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm text-left">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th v-for="col in currentTab.columns" :key="col.key" class="py-2 pr-4 font-medium">{{ col.label }}</th>
                <th class="py-2 pr-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id" class="border-b border-slate-800/80 text-slate-200">
                <td v-for="col in currentTab.columns" :key="col.key" class="py-3 pr-4">
                  {{ row[col.key] ?? '—' }}
                </td>
                <td class="py-3 pr-4">
                  <button
                    class="text-red-400 hover:text-red-300 font-medium disabled:opacity-50"
                    :disabled="deletingId === row.id"
                    @click="deleteItem(row)"
                  >{{ deletingId === row.id ? 'Deleting…' : 'Delete' }}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

type TabId = 'customers' | 'contracts' | 'documents' | 'pricebook'

const route = useRoute()
const router = useRouter()

const tabs: Array<{
  id: TabId
  label: string
  singular: string
  endpoint: string
  listKey: string
  canCreate: boolean
  columns: Array<{ key: string; label: string }>
  createBody: () => Record<string, unknown>
}> = [
  {
    id: 'customers',
    label: 'Customers',
    singular: 'Customer',
    endpoint: '/api/company/customers',
    listKey: 'customers',
    canCreate: true,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'company', label: 'Company' },
      { key: 'status', label: 'Status' },
    ],
    createBody: () => ({
      name: 'New Customer',
      email: `customer${Date.now().toString().slice(-4)}@example.com`,
      company: 'Customer Co',
    }),
  },
  {
    id: 'contracts',
    label: 'Service Contracts',
    singular: 'Contract',
    endpoint: '/api/company/service-contracts',
    listKey: 'contracts',
    canCreate: true,
    columns: [
      { key: 'customer', label: 'Customer' },
      { key: 'title', label: 'Title' },
      { key: 'startDate', label: 'Start' },
      { key: 'endDate', label: 'End' },
      { key: 'totalValue', label: 'Value' },
      { key: 'status', label: 'Status' },
    ],
    createBody: () => ({
      customerName: 'Customer',
      title: 'Service Agreement',
      startDate: new Date().toISOString().slice(0, 10),
      totalValue: 0,
    }),
  },
  {
    id: 'documents',
    label: 'Documents',
    singular: 'Document',
    endpoint: '/api/company/documents',
    listKey: 'documents',
    canCreate: true,
    columns: [
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { key: 'fileType', label: 'Type' },
      { key: 'uploadedAt', label: 'Uploaded' },
    ],
    createBody: () => ({
      title: `Document ${new Date().toLocaleDateString()}`,
      category: 'general',
      description: '',
    }),
  },
  {
    id: 'pricebook',
    label: 'Pricebook',
    singular: 'Item',
    endpoint: '/api/company/pricebook',
    listKey: 'items',
    canCreate: true,
    columns: [
      { key: 'serviceName', label: 'Service' },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate' },
      { key: 'description', label: 'Description' },
    ],
    createBody: () => ({
      serviceName: 'Field Service',
      unit: 'hour',
      rate: 125,
      description: '',
    }),
  },
]

const validTabIds = new Set(tabs.map((t) => t.id))

function tabFromQuery(): TabId {
  const q = String(route.query.tab || '')
  return validTabIds.has(q as TabId) ? (q as TabId) : 'customers'
}

const activeTab = ref<TabId>(tabFromQuery())
const rows = ref<Record<string, any>[]>([])
const counts = ref<Record<string, number>>({})
const loading = ref(false)
const deletingId = ref<number | null>(null)
const error = ref('')

const currentTab = computed(() => tabs.find((t) => t.id === activeTab.value)!)

function authHeaders() {
  const token = localStorage.getItem('auth_token') || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function loadTab(id: TabId = activeTab.value) {
  const tab = tabs.find((t) => t.id === id)!
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(tab.endpoint, { headers: authHeaders() })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const list = Array.isArray(data) ? data : data[tab.listKey] || []
    rows.value = list
    counts.value = { ...counts.value, [id]: list.length }
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function switchTab(id: TabId) {
  activeTab.value = id
  if (route.query.tab !== id) {
    router.replace({ path: route.path, query: { ...route.query, tab: id } })
  }
  await loadTab(id)
}

async function createItem() {
  const tab = currentTab.value
  if (!tab.canCreate) return
  try {
    const res = await fetch(tab.endpoint, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(tab.createBody()),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    await loadTab()
  } catch (e: any) {
    error.value = e.message || 'Create failed'
  }
}

async function deleteItem(row: Record<string, any>) {
  const tab = currentTab.value
  const label = row.name || row.title || row.serviceName || `${tab.singular} #${row.id}`
  if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return
  deletingId.value = Number(row.id)
  error.value = ''
  try {
    const res = await fetch(`${tab.endpoint}/${row.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    rows.value = rows.value.filter((item) => item.id !== row.id)
    counts.value = { ...counts.value, [tab.id]: rows.value.length }
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  } finally {
    deletingId.value = null
  }
}

watch(
  () => route.query.tab,
  async () => {
    const next = tabFromQuery()
    if (next !== activeTab.value) {
      activeTab.value = next
      await loadTab(next)
    }
  }
)

onMounted(async () => {
  activeTab.value = tabFromQuery()
  await loadTab(activeTab.value)
  for (const tab of tabs) {
    if (tab.id === activeTab.value) continue
    try {
      const res = await fetch(tab.endpoint, { headers: authHeaders() })
      if (!res.ok) continue
      const data = await res.json()
      const list = Array.isArray(data) ? data : data[tab.listKey] || []
      counts.value = { ...counts.value, [tab.id]: list.length }
    } catch {
      /* ignore */
    }
  }
})
</script>
